/**
 * Example Models Library
 *
 * Pre-defined example IDF models for learning and templates.
 */

import type { ExampleModel, WeatherFileInfo } from './types';

/**
 * Bundled weather files.
 * Denver is bundled locally, others are fetched from climate.onebuilding.org via proxy.
 */
export const WEATHER_FILES: WeatherFileInfo[] = [
  {
    id: 'denver',
    name: 'Denver, CO (TMY3)',
    url: '/weather/denver.epw',
    location: 'Denver, Colorado, USA',
    latitude: 39.833,
    longitude: -104.65,
    elevation: 1650,
    timezone: -7,
    source: 'TMY3',
  },
  {
    id: 'chicago',
    name: 'Chicago, IL (TMY3)',
    url: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/IL_Illinois/USA_IL_Chicago.OHare.Intl.AP.725300_TMY3.zip',
    location: 'Chicago, Illinois, USA',
    latitude: 41.98,
    longitude: -87.92,
    elevation: 201,
    timezone: -6,
    source: 'TMY3',
  },
  {
    id: 'miami',
    name: 'Miami, FL (TMY3)',
    url: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/FL_Florida/USA_FL_Miami.Intl.AP.722020_TMY3.zip',
    location: 'Miami, Florida, USA',
    latitude: 25.82,
    longitude: -80.3,
    elevation: 4,
    timezone: -5,
    source: 'TMY3',
  },
  {
    id: 'phoenix',
    name: 'Phoenix, AZ (TMY3)',
    url: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/AZ_Arizona/USA_AZ_Phoenix-Sky.Harbor.Intl.AP.722780_TMY3.zip',
    location: 'Phoenix, Arizona, USA',
    latitude: 33.45,
    longitude: -111.98,
    elevation: 337,
    timezone: -7,
    source: 'TMY3',
  },
  {
    id: 'seattle',
    name: 'Seattle, WA (TMY3)',
    url: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/WA_Washington/USA_WA_Seattle-Tacoma.Intl.AP.727930_TMY3.zip',
    location: 'Seattle, Washington, USA',
    latitude: 47.45,
    longitude: -122.3,
    elevation: 137,
    timezone: -8,
    source: 'TMY3',
  },
  {
    id: 'new-york',
    name: 'New York, NY (TMY3)',
    url: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/NY_New_York/USA_NY_New.York-Kennedy.Intl.AP.744860_TMY3.zip',
    location: 'New York, New York, USA',
    latitude: 40.65,
    longitude: -73.78,
    elevation: 6,
    timezone: -5,
    source: 'TMY3',
  },
];

/**
 * Example models library.
 */
export const EXAMPLE_MODELS: ExampleModel[] = [
  // Tutorials
  {
    id: 'simple-box',
    name: 'Simple Box Building',
    description:
      'A single-zone rectangular building with basic envelope construction. Perfect for learning EnergyPlus fundamentals.',
    category: 'tutorials',
    complexity: 'simple',
    features: ['Single zone', 'Ideal loads', 'Basic construction'],
    thumbnail: '/examples/thumbnails/simple-box.svg',
    idfUrl: '/examples/simple-box.idf',
    weatherUrl: '/weather/denver.epw',
  },
  {
    id: 'shoebox',
    name: 'Shoebox Model',
    description:
      'A simple shoebox model demonstrating basic geometry and thermal zones. Great starting point for new users.',
    category: 'tutorials',
    complexity: 'simple',
    features: ['Rectangular geometry', 'Single zone', 'Basic schedules'],
    thumbnail: '/examples/thumbnails/shoebox.svg',
    idfUrl: '/examples/shoebox.idf',
    weatherUrl: '/weather/denver.epw',
  },
  {
    id: 'hvac-intro',
    name: 'HVAC Introduction',
    description:
      'Introduction to HVAC modeling with a simple packaged terminal air conditioner (PTAC) system.',
    category: 'tutorials',
    complexity: 'simple',
    features: ['PTAC system', 'Zone thermostat', 'Basic schedules'],
    thumbnail: '/examples/thumbnails/hvac-intro.svg',
    idfUrl: '/examples/hvac-intro.idf',
    weatherUrl: '/weather/denver.epw',
  },

  // Residential
  {
    id: 'single-family-home',
    name: 'Single Family Home',
    description:
      'A typical single-family residential building with multiple zones and a furnace/AC split system.',
    category: 'residential',
    complexity: 'medium',
    features: ['Multi-zone', 'Split system', 'Natural ventilation', 'Daylighting'],
    thumbnail: '/examples/thumbnails/single-family.svg',
    idfUrl: '/examples/single-family-home.idf',
    weatherUrl: '/weather/denver.epw',
  },
  {
    id: 'apartment',
    name: 'Apartment Building',
    description:
      'A multi-story apartment building with individual zone HVAC and shared common areas.',
    category: 'residential',
    complexity: 'complex',
    features: ['Multi-story', 'Individual zone control', 'Common areas'],
    thumbnail: '/examples/thumbnails/apartment.svg',
    idfUrl: '/examples/apartment.idf',
    weatherUrl: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/NY_New_York/USA_NY_New.York-Kennedy.Intl.AP.744860_TMY3.zip',
  },

  // Commercial
  {
    id: 'small-office',
    name: 'Small Office',
    description:
      'DOE prototype small office building with 5 zones and a packaged rooftop unit with economizer.',
    category: 'commercial',
    complexity: 'medium',
    features: ['5 zones', 'Packaged rooftop', 'Economizer', 'Daylighting'],
    thumbnail: '/examples/thumbnails/small-office.svg',
    idfUrl: '/examples/small-office.idf',
    weatherUrl: '/weather/denver.epw',
  },
  {
    id: 'medium-office',
    name: 'Medium Office',
    description:
      'DOE prototype medium office building with multiple floors, VAV system, and central plant.',
    category: 'commercial',
    complexity: 'complex',
    features: ['Multi-story', 'VAV system', 'Chiller/Boiler', 'Demand control ventilation'],
    thumbnail: '/examples/thumbnails/medium-office.svg',
    idfUrl: '/examples/medium-office.idf',
    weatherUrl: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/IL_Illinois/USA_IL_Chicago.OHare.Intl.AP.725300_TMY3.zip',
  },
  {
    id: 'retail-store',
    name: 'Retail Store',
    description: 'A standalone retail building with open floor plan and high internal loads.',
    category: 'commercial',
    complexity: 'medium',
    features: ['Open floor plan', 'High lighting loads', 'Display refrigeration'],
    thumbnail: '/examples/thumbnails/retail.svg',
    idfUrl: '/examples/retail-store.idf',
    weatherUrl: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/FL_Florida/USA_FL_Miami.Intl.AP.722020_TMY3.zip',
  },
  {
    id: 'warehouse',
    name: 'Warehouse',
    description:
      'A large warehouse with loading docks, minimal HVAC, and high infiltration.',
    category: 'commercial',
    complexity: 'simple',
    features: ['Large volume', 'Unit heaters', 'Loading docks'],
    thumbnail: '/examples/thumbnails/warehouse.svg',
    idfUrl: '/examples/warehouse.idf',
    weatherUrl: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/AZ_Arizona/USA_AZ_Phoenix-Sky.Harbor.Intl.AP.722780_TMY3.zip',
  },

  // HVAC Examples
  {
    id: 'vav-reheat',
    name: 'VAV with Reheat',
    description:
      'Variable air volume system with hot water reheat terminals, demonstrating advanced air system modeling.',
    category: 'hvac-examples',
    complexity: 'complex',
    features: ['VAV system', 'Hot water reheat', 'Central air handling'],
    thumbnail: '/examples/thumbnails/vav-reheat.svg',
    idfUrl: '/examples/vav-reheat.idf',
    weatherUrl: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/IL_Illinois/USA_IL_Chicago.OHare.Intl.AP.725300_TMY3.zip',
  },
  {
    id: 'vrf-system',
    name: 'VRF System',
    description:
      'Variable refrigerant flow system with heat recovery, ideal for buildings with simultaneous heating and cooling.',
    category: 'hvac-examples',
    complexity: 'complex',
    features: ['VRF system', 'Heat recovery', 'Zoned control'],
    thumbnail: '/examples/thumbnails/vrf.svg',
    idfUrl: '/examples/vrf-system.idf',
    weatherUrl: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/WA_Washington/USA_WA_Seattle-Tacoma.Intl.AP.727930_TMY3.zip',
  },
  {
    id: 'radiant-floor',
    name: 'Radiant Floor Heating',
    description: 'Low-temperature radiant floor heating system with hydronic loops.',
    category: 'hvac-examples',
    complexity: 'medium',
    features: ['Radiant floor', 'Hydronic system', 'Condensing boiler'],
    thumbnail: '/examples/thumbnails/radiant.svg',
    idfUrl: '/examples/radiant-floor.idf',
    weatherUrl: '/weather/denver.epw',
  },
  {
    id: 'ground-source-hp',
    name: 'Ground Source Heat Pump',
    description:
      'Ground source heat pump system with vertical borehole heat exchangers.',
    category: 'hvac-examples',
    complexity: 'complex',
    features: ['GSHP', 'Vertical boreholes', 'Water-to-air HP'],
    thumbnail: '/examples/thumbnails/gshp.svg',
    idfUrl: '/examples/ground-source-hp.idf',
    weatherUrl: 'https://climate.onebuilding.org/WMO_Region_4_North_and_Central_America/USA_United_States_of_America/NY_New_York/USA_NY_New.York-Kennedy.Intl.AP.744860_TMY3.zip',
  },
];

/**
 * Get example models by category.
 */
export function getExamplesByCategory(category: ExampleModel['category']): ExampleModel[] {
  return EXAMPLE_MODELS.filter((model) => model.category === category);
}

/**
 * Get example models by complexity.
 */
export function getExamplesByComplexity(complexity: ExampleModel['complexity']): ExampleModel[] {
  return EXAMPLE_MODELS.filter((model) => model.complexity === complexity);
}

/**
 * Search example models by name, description, or features.
 */
export function searchExamples(query: string): ExampleModel[] {
  const lowerQuery = query.toLowerCase();
  return EXAMPLE_MODELS.filter(
    (model) =>
      model.name.toLowerCase().includes(lowerQuery) ||
      model.description.toLowerCase().includes(lowerQuery) ||
      model.features.some((f) => f.toLowerCase().includes(lowerQuery))
  );
}

/**
 * Get example model by ID.
 */
export function getExampleById(id: string): ExampleModel | undefined {
  return EXAMPLE_MODELS.find((model) => model.id === id);
}

/**
 * Get weather file by ID.
 */
export function getWeatherFileById(id: string): WeatherFileInfo | undefined {
  return WEATHER_FILES.find((wf) => wf.id === id);
}

/**
 * Get complexity level display text.
 */
export function getComplexityLabel(complexity: ExampleModel['complexity']): string {
  switch (complexity) {
    case 'simple':
      return 'Beginner';
    case 'medium':
      return 'Intermediate';
    case 'complex':
      return 'Advanced';
  }
}

/**
 * Get category display text.
 */
export function getCategoryLabel(category: ExampleModel['category']): string {
  switch (category) {
    case 'tutorials':
      return 'Tutorials';
    case 'residential':
      return 'Residential';
    case 'commercial':
      return 'Commercial';
    case 'hvac-examples':
      return 'HVAC Examples';
  }
}
